/**
 * @fileoverview ClubOS Youth Academy — Transactions
 * Unified income ledger: monthly fees, admissions, donations, kits, services.
 * Per-player dues table + global "Add Transaction" for any income type.
 */

import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import PropTypes from "prop-types";
import { academyService } from "api";
import FootballLoader from "components/FootballLoader";
import { buildBrandedReport, addWatermarkToPdf } from "config/brandingHelpers";
import { TYPOGRAPHY, COLORS } from "config/branding";

const MONTHS       = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_SHORT  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PAYMENT_MODES = ["Cash", "UPI", "Online", "Cheque", "NEFT"];
const FEE_STATUSES  = ["Paid", "Partial", "Due", "Waived"];
const STATUS_COLOR  = { Paid: "success", Partial: "warning", Due: "error", Waived: "info" };
const FEE_PLAN_COLOR = { Standard: "info", "Scholarship (25%)": "warning", "Full Waiver": "success" };

const TRANSACTION_TYPES = [
  { value: "Monthly",   label: "Monthly Fee" },
  { value: "Admission", label: "Admission Fee" },
  { value: "Donation",  label: "Donation" },
  { value: "Kit",       label: "Kit / Equipment" },
  { value: "Service",   label: "Service" },
  { value: "Other",     label: "Other" },
];

const emptyPayForm = {
  amount: "", paymentMode: "UPI",
  paymentDate: new Date().toISOString().slice(0, 10),
  transactionRef: "", notes: "",
};

const emptyEditForm = {
  totalAmount: "", paidAmount: "", feeStatus: "Due", paymentMode: "UPI",
  paymentDate: new Date().toISOString().slice(0, 10),
  transactionRef: "", notes: "",
};

const emptyTxnForm = {
  playerId: "", feeType: "Monthly", amount: "",
  paymentMode: "Cash",
  paymentDate: new Date().toISOString().slice(0, 10),
  transactionRef: "", notes: "",
};

function fmtMY(month, year) {
  return `${MONTH_SHORT[month - 1]} ${year}`;
}

// ── Receipt PDF ───────────────────────────────────────────────────────────────
async function generateFeeReceipt({
  transactionId,
  playerId, playerCode, playerName, batchName, ageGroup,
  feeType, feeMonth, feeYear, monthsLabel,
  paidAmount, totalAmount, discountAmount,
  paymentMode, paymentDate, transactionRef,
}) {
  const receiptNo = transactionId ?? (() => {
    const _rKey = `${playerId ?? "X"}-${feeYear}-${String(feeMonth ?? 0).padStart(2, "0")}`;
    let _rh = 0;
    for (let i = 0; i < _rKey.length; i++) _rh = (Math.imul(31, _rh) + _rKey.charCodeAt(i)) | 0;
    return `TXN-${feeYear}-${String(Math.abs(_rh) % 1000000).padStart(6, "0")}`;
  })();

  const { jsPDF } = await import("jspdf");
  const autoTable  = (await import("jspdf-autotable")).default;
  const { ORGANIZATION, PDF } = await import("config/branding");
  const { addWatermarkToPdf: applyLogo } = await import("config/brandingHelpers");
  const ORG_NAME = ORGANIZATION.name;
  const ORG_TAGLINE = ORGANIZATION.tagline;
  const BRAND_COLOR = PDF.brandColor;
  const HEADER_TEXT_COLOR = PDF.headerTextColor;

  const feePeriodLabel = monthsLabel
    ?? (feeMonth ? `${MONTHS[feeMonth - 1]} ${feeYear}` : `${feeYear}`);

  const typeLabel = TRANSACTION_TYPES.find((t) => t.value === feeType)?.label ?? feeType ?? "Transaction";

  let admissionItems = [];
  if (feeType === "Monthly" && playerId) {
    try {
      const items = await academyService.getAdmissionItems(playerId);
      admissionItems = Array.isArray(items) ? items : [];
    } catch { /* silent */ }
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5" });
  const W = doc.internal.pageSize.getWidth();

  const nowFull = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
  const payDateFmt = paymentDate
    ? new Date(paymentDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  doc.setFillColor(...BRAND_COLOR);
  doc.rect(0, 0, W, 28, "F");

  try {
    const { loadMscLogo } = await import("config/brandingHelpers");
    const logoData = await loadMscLogo(true);
    if (logoData) {
      doc.addImage(logoData, "PNG", 4, 5, 18, 18);
    }
  } catch { /* decorative */ }

  doc.setTextColor(...HEADER_TEXT_COLOR);
  doc.setFont("helvetica", "bold");   doc.setFontSize(13);
  doc.text(ORG_NAME, (22 + W) / 2, 11, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  if (ORG_TAGLINE) doc.text(ORG_TAGLINE, (22 + W) / 2, 17, { align: "center" });
  doc.setFont("helvetica", "bold");   doc.setFontSize(9);
  doc.text("TRANSACTION RECEIPT", (22 + W) / 2, 24, { align: "center" });

  let y = 33;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(80);
  doc.text("Transaction ID:", 10, y);
  doc.setFont("helvetica", "bold");   doc.setFontSize(7.5); doc.setTextColor(30, 30, 30);
  doc.text(receiptNo, 33, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(80);
  doc.text("Payment Date:", W - 55, y);
  doc.setFont("helvetica", "bold");   doc.setFontSize(7.5); doc.setTextColor(30, 30, 30);
  doc.text(payDateFmt, W - 10, y, { align: "right" });

  y += 4;
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(140);
  doc.text(`Generated: ${nowFull}`, W - 10, y, { align: "right" });

  y += 4;
  doc.setDrawColor(...BRAND_COLOR); doc.setLineWidth(0.4);
  doc.line(10, y, W - 10, y);

  y += 5;
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(44, 62, 80);
  doc.text("STUDENT DETAILS", 10, y);

  const colL = 10;
  const colR = W / 2 + 4;
  const colMaxW = W / 2 - 14;

  const lbl = (text, x, yPos) => {
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(130);
    doc.text(text, x, yPos);
  };
  const val = (text, x, yPos) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(30, 30, 30);
    doc.text(String(text ?? "—"), x, yPos, { maxWidth: colMaxW });
  };

  y += 5;
  lbl("Student Name", colL, y); lbl("Player Code", colR, y);
  y += 4;
  val(playerName, colL, y);     val(playerCode ?? "—", colR, y);

  y += 7;
  lbl("Batch", colL, y);        lbl("Transaction Type", colR, y);
  y += 4;
  val(batchName ?? "—", colL, y); val(typeLabel, colR, y);

  y += 7;
  lbl("Age Group", colL, y);    lbl("Period / Reference", colR, y);
  y += 4;
  val(ageGroup ?? "—", colL, y); val(feePeriodLabel, colR, y);

  y += 7;
  lbl("Payment Mode", colL, y);  lbl("Transaction Ref", colR, y);
  y += 4;
  val(paymentMode ?? "—", colL, y); val(transactionRef ?? "—", colR, y);

  y += 6;
  doc.setDrawColor(200); doc.setLineWidth(0.3);
  doc.line(10, y, W - 10, y);

  y += 4;
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(44, 62, 80);
  doc.text("AMOUNT BREAKDOWN", 10, y);
  y += 3;

  const discount      = Number(discountAmount ?? 0);
  const mainAmount    = Number(totalAmount);
  const admissionTotal = admissionItems.reduce((s, i) => s + Number(i.amount), 0);
  const grandPayable  = mainAmount + admissionTotal - discount;
  const outstanding   = grandPayable - Number(paidAmount);

  const rs = (n) => `Rs.${Number(n).toLocaleString("en-IN")}`;

  const tableBody = [
    ["1", `${typeLabel}${feeMonth ? ` — ${feePeriodLabel}` : ""}`, rs(mainAmount)],
    ...admissionItems.map((item, idx) => [
      String(idx + 2),
      `${item.itemType}${item.quantity > 1 ? ` x ${item.quantity}` : ""}`,
      rs(item.amount),
    ]),
  ];

  const footRows = [];
  if (discount > 0) footRows.push(["", "Discount", `-${rs(discount)}`]);
  footRows.push(["", "Total Payable", rs(grandPayable)]);
  footRows.push(["", "Amount Paid",   rs(paidAmount)]);
  footRows.push(["", outstanding > 0 ? "Outstanding Balance" : "Balance",
    outstanding > 0 ? rs(outstanding) : `${rs(0)}\nPAID IN FULL`,
  ]);

  autoTable(doc, {
    startY: y,
    head: [["#", "Description", "Amount"]],
    body: tableBody,
    foot: footRows,
    styles:     { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: BRAND_COLOR, textColor: HEADER_TEXT_COLOR, fontStyle: "bold", fontSize: 7 },
    footStyles: { fillColor: [240, 248, 240], textColor: [30, 30, 30], fontStyle: "bold", fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 8,  halign: "center" },
      1: { cellWidth: 85 },
      2: { cellWidth: 35, halign: "right" },
    },
    margin: { left: 10, right: 10 },
    showFoot: "lastPage",
  });

  const safeName = (playerName ?? "student").replace(/[^a-z0-9]/gi, "_").toLowerCase();
  doc.save(`receipt-${safeName}-${receiptNo}.pdf`);

  void applyLogo;
}

// ── Dues PDF report ────────────────────────────────────────────────────────────
async function downloadDuesPDF(dues) {
  const totalDue = dues.reduce((s, d) => s + Number(d.totalDue), 0);
  const { doc, autoTable, startY, headStyles, altStyles } = await buildBrandedReport(
    "Fee Dues Report",
    [
      `Players: ${dues.length}  |  Total Outstanding: Rs.${totalDue.toLocaleString("en-IN")}`,
      `Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
    ],
  );

  autoTable(doc, {
    startY,
    head: [["#", "Player Code", "Student", "Batch", "Monthly Fee", "Paid Up To", "Months Behind", "Total Due (Rs)"]],
    body: dues.map((d, i) => [
      i + 1,
      d.playerCode ?? "—",
      d.playerName ?? "",
      d.batchName  ?? "—",
      Number(d.monthlyFee).toLocaleString("en-IN"),
      fmtMY(d.paidUpToMonth, d.paidUpToYear),
      d.monthsOwed,
      Number(d.totalDue).toLocaleString("en-IN"),
    ]),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { ...headStyles, fontSize: 7 },
    alternateRowStyles: altStyles,
    columnStyles: {
      3: { halign: "right" },
      6: { halign: "center" },
      7: { halign: "right", fontStyle: "bold" },
    },
  });

  await addWatermarkToPdf(doc);
  doc.save(`fees-dues-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Per-player history PDF ─────────────────────────────────────────────────────
async function downloadPlayerHistoryPDF(playerName, history) {
  const { doc, autoTable, startY, headStyles, altStyles } = await buildBrandedReport(
    `Transaction History — ${playerName}`,
    [`Records: ${history.length}`],
  );

  autoTable(doc, {
    startY,
    head: [[
      "Txn ID", "Type", "Period", "Total (Rs)", "Paid (Rs)", "Outstanding (Rs)", "Status",
      "Mode", "Ref", "Notes",
    ]],
    body: history.map((f) => [
      f.transactionId ?? "—",
      TRANSACTION_TYPES.find((t) => t.value === f.feeType)?.label ?? f.feeType,
      f.feeType === "Admission"
        ? `Admission ${f.feeYear}`
        : (f.feeMonth ? `${MONTHS[(f.feeMonth ?? 1) - 1]} ${f.feeYear}` : `${f.feeYear}`),
      Number(f.totalAmount).toLocaleString("en-IN"),
      Number(f.paidAmount).toLocaleString("en-IN"),
      Number(f.outstandingBalance ?? 0).toLocaleString("en-IN"),
      f.feeStatus ?? "",
      f.paymentMode ?? "—",
      f.transactionRef ?? "—",
      f.notes ?? "—",
    ]),
    styles: { fontSize: 6.5, cellPadding: 1.5 },
    headStyles: { ...headStyles, fontSize: 6.5 },
    alternateRowStyles: altStyles,
    columnStyles: {
      0: { fontStyle: "bold", textColor: [26, 115, 232] },
    },
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(8); doc.setTextColor(150);
  doc.text("This report is confidential.", 14, finalY);

  await addWatermarkToPdf(doc);
  const safeName = playerName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  doc.save(`txn-history-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Per-player history dialog ──────────────────────────────────────────────────
function PlayerHistoryDialog({ open, onClose, player, onEdit, onDeleted }) {
  const [history, setHistory]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const reload = () => {
    if (!player) return;
    setLoading(true);
    academyService.getFees({ playerId: player.playerId })
      .then((d) => setHistory(Array.isArray(d) ? [...d].sort((a, b) => {
        if (b.feeYear !== a.feeYear) return b.feeYear - a.feeYear;
        return (b.feeMonth ?? 0) - (a.feeMonth ?? 0);
      }) : []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && player) reload(); }, [open, player]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await academyService.deleteFee(deleteTarget.id);
      setDeleteTarget(null);
      reload();
      if (onDeleted) onDeleted();
    } catch { reload(); }
    finally { setDeleting(false); }
  };

  const totalPaid = history.reduce((s, f) => s + Number(f.paidAmount), 0);
  const totalDue  = history.reduce((s, f) => s + Number(f.outstandingBalance ?? 0), 0);

  const periodLabel = (f) => {
    if (!f) return "";
    if (f.feeType === "Admission") return `Admission ${f.feeYear}`;
    if (f.feeMonth) return `${MONTHS[(f.feeMonth ?? 1) - 1]} ${f.feeYear}`;
    return `${f.feeYear}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: "85vh" } }}>
      <DialogTitle>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">{player?.playerName}</SoftTypography>
            <SoftTypography variant="caption" color="text">{player?.batchName ?? "No batch"} · Transaction History</SoftTypography>
          </SoftBox>
          <IconButton size="small" onClick={onClose}><Icon>close</Icon></IconButton>
        </SoftBox>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <SoftBox display="flex" gap={3} px={2.5} py={1.5}
          sx={{ backgroundColor: "#fafbff", borderBottom: "1px solid #f0f0f0" }}>
          <SoftBox>
            <SoftTypography variant="caption" color="text">Total Paid</SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#2e7d32" }}>
              ₹{totalPaid.toLocaleString("en-IN")}
            </SoftTypography>
          </SoftBox>
          <Divider orientation="vertical" flexItem />
          <SoftBox>
            <SoftTypography variant="caption" color="text">Outstanding</SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: totalDue > 0 ? "#d32f2f" : "#2e7d32" }}>
              ₹{totalDue.toLocaleString("en-IN")}
            </SoftTypography>
          </SoftBox>
          <Divider orientation="vertical" flexItem />
          <SoftBox>
            <SoftTypography variant="caption" color="text">Records</SoftTypography>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">{history.length}</SoftTypography>
          </SoftBox>
        </SoftBox>

        <SoftBox display="flex" alignItems="center" px={2.5} py={1}
          sx={{ borderBottom: "1px solid #f0f0f0", backgroundColor: "#f7f8fa" }}>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 115px" }}>TXN ID</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px" }}>TYPE</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px" }}>PERIOD</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "right" }}>TOTAL</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "right" }}>PAID</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "right" }}>DUE</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 70px", textAlign: "center" }}>STATUS</SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>MODE</SoftTypography>
          <SoftBox sx={{ width: 88 }} />
        </SoftBox>

        {loading ? (
          <FootballLoader label="Loading history…" />
        ) : history.length === 0 ? (
          <SoftBox p={4} textAlign="center">
            <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>No records found</SoftTypography>
          </SoftBox>
        ) : (
          history.map((f) => (
            <SoftBox key={f.id} display="flex" alignItems="center" px={2.5} py={1.25}
              sx={{ borderBottom: "1px solid #f9f9f9", "&:hover": { backgroundColor: "rgba(26,115,232,0.02)" } }}>
              <SoftTypography variant="caption" fontWeight="bold"
                sx={{ flex: "0 0 115px", color: "#1a73e8", fontFamily: "monospace", fontSize: "0.65rem" }}>
                {f.transactionId ?? "—"}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 110px" }}>
                <SoftBadge
                  badgeContent={TRANSACTION_TYPES.find((t) => t.value === f.feeType)?.label ?? f.feeType}
                  color="info" size="xs" container />
              </SoftBox>
              <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ flex: "0 0 130px" }}>
                {periodLabel(f)}
              </SoftTypography>
              <SoftTypography variant="caption" color="dark" sx={{ flex: "0 0 80px", textAlign: "right" }}>
                ₹{Number(f.totalAmount).toLocaleString("en-IN")}
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ flex: "0 0 80px", textAlign: "right", color: "#2e7d32" }}>
                ₹{Number(f.paidAmount).toLocaleString("en-IN")}
              </SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold"
                sx={{ flex: "0 0 80px", textAlign: "right", color: Number(f.outstandingBalance) > 0 ? "#d32f2f" : "#2e7d32" }}>
                ₹{Number(f.outstandingBalance ?? 0).toLocaleString("en-IN")}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 70px", textAlign: "center" }}>
                <SoftBadge badgeContent={f.feeStatus} color={STATUS_COLOR[f.feeStatus] ?? "info"} size="xs" container />
              </SoftBox>
              <SoftBox sx={{ flex: 1 }}>
                <SoftTypography variant="caption" color="text">
                  {f.paymentMode ?? "—"}{f.transactionRef ? ` · ${f.transactionRef}` : ""}
                </SoftTypography>
              </SoftBox>
              {Number(f.paidAmount) > 0 && (
                <IconButton size="small" title="Print Receipt"
                  onClick={() => generateFeeReceipt({
                    transactionId:  f.transactionId,
                    playerId:       player?.playerId,
                    playerCode:     player?.playerCode,
                    playerName:     player?.playerName,
                    batchName:      player?.batchName,
                    ageGroup:       player?.ageGroup ?? null,
                    feeType:        f.feeType,
                    feeMonth:       f.feeMonth,
                    feeYear:        f.feeYear,
                    paidAmount:     Number(f.paidAmount),
                    totalAmount:    Number(f.totalAmount),
                    discountAmount: Number(f.discountAmount ?? 0),
                    paymentMode:    f.paymentMode,
                    paymentDate:    f.paymentDate,
                    transactionRef: f.transactionRef,
                  }).catch(console.error)}
                  sx={{ width: 28, height: 28 }}>
                  <Icon sx={{ fontSize: "0.9rem", color: "#757575" }}>receipt</Icon>
                </IconButton>
              )}
              <IconButton size="small" onClick={() => onEdit(f)} sx={{ width: 28, height: 28 }}>
                <Icon sx={{ fontSize: "0.9rem", color: "#1a73e8" }}>edit</Icon>
              </IconButton>
              <IconButton size="small" title="Delete" onClick={() => setDeleteTarget(f)} sx={{ width: 28, height: 28 }}>
                <Icon sx={{ fontSize: "0.9rem", color: "#d32f2f" }}>delete</Icon>
              </IconButton>
            </SoftBox>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <SoftButton variant="outlined" color="primary" size="small"
          startIcon={<Icon>download</Icon>}
          disabled={history.length === 0}
          onClick={() => downloadPlayerHistoryPDF(player?.playerName ?? "player", history)}>
          Download History PDF
        </SoftButton>
        <SoftButton variant="text" color="secondary" onClick={onClose}>Close</SoftButton>
      </DialogActions>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Transaction Record</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Delete transaction <strong>{deleteTarget?.transactionId ?? ""}</strong>?
            This cannot be undone.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</SoftButton>
          <SoftButton variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
PlayerHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  player: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
};

// ── Edit transaction record dialog ─────────────────────────────────────────────
function EditFeeDialog({ open, onClose, record, onSaved }) {
  const [form, setForm]     = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (!record) return;
    setForm({
      totalAmount:    String(record.totalAmount ?? ""),
      paidAmount:     String(record.paidAmount ?? ""),
      feeStatus:      record.feeStatus ?? "Due",
      paymentMode:    record.paymentMode ?? "UPI",
      paymentDate:    record.paymentDate ? String(record.paymentDate).slice(0, 10)
                        : new Date().toISOString().slice(0, 10),
      transactionRef: record.transactionRef ?? "",
      notes:          record.notes ?? "",
    });
    setError("");
  }, [record]);

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.totalAmount || isNaN(Number(form.totalAmount))) {
      setError("Total amount must be a valid number."); return;
    }
    setSaving(true); setError("");
    try {
      await academyService.updateFee(record.id, {
        totalAmount:    Number(form.totalAmount),
        paidAmount:     Number(form.paidAmount || 0),
        feeStatus:      form.feeStatus,
        paymentMode:    form.paymentMode,
        paymentDate:    form.paymentDate || null,
        transactionRef: form.transactionRef || null,
        notes:          form.notes || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.detail ?? e?.title ?? "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <SoftBox>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">Edit Transaction Record</SoftTypography>
          {record && (
            <SoftTypography variant="caption" color="text">
              {record.playerName} · {record.transactionId ?? ""} ·{" "}
              {TRANSACTION_TYPES.find((t) => t.value === record.feeType)?.label ?? record.feeType}
            </SoftTypography>
          )}
        </SoftBox>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          <SoftTypography variant="caption">Audit trail is captured automatically.</SoftTypography>
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth required label="Total Amount (₹)" type="number" value={form.totalAmount}
              onChange={f("totalAmount")} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Paid Amount (₹)" type="number" value={form.paidAmount}
              onChange={f("paidAmount")} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Status" value={form.feeStatus} onChange={f("feeStatus")} size="small">
              {FEE_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Payment Mode" value={form.paymentMode} onChange={f("paymentMode")} size="small">
              {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Payment Date" type="date" value={form.paymentDate}
              onChange={f("paymentDate")} size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Transaction Reference" value={form.transactionRef}
              onChange={f("transactionRef")} size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" value={form.notes}
              onChange={f("notes")} size="small" multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <SoftButton variant="text" color="secondary" onClick={onClose}>Cancel</SoftButton>
        <SoftButton variant="gradient" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
EditFeeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.shape({
    id: PropTypes.string,
    playerName: PropTypes.string,
    transactionId: PropTypes.string,
    feeType: PropTypes.string,
    feeMonth: PropTypes.number,
    feeYear: PropTypes.number,
    totalAmount: PropTypes.number,
    paidAmount: PropTypes.number,
    feeStatus: PropTypes.string,
    paymentMode: PropTypes.string,
    paymentDate: PropTypes.string,
    transactionRef: PropTypes.string,
    notes: PropTypes.string,
  }),
  onSaved: PropTypes.func.isRequired,
};

// ── Add Transaction dialog ─────────────────────────────────────────────────────
function AddTransactionDialog({ open, onClose, onSaved, dues }) {
  const [form, setForm]     = useState(emptyTxnForm);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [playerInput, setPlayerInput] = useState("");

  useEffect(() => { if (!open) { setForm(emptyTxnForm); setError(""); setPlayerInput(""); } }, [open]);

  const selectedPlayer = dues.find((d) => d.playerId === form.playerId) ?? null;
  const feeNum = selectedPlayer ? Number(selectedPlayer.monthlyFee) : 1;
  const amtNum = Number(form.amount);
  const isMonthly = form.feeType === "Monthly";
  const isValidMonthly = isMonthly ? (amtNum > 0 && feeNum > 0 && amtNum % feeNum === 0) : true;

  const monthsPreview = () => {
    if (!isMonthly || !selectedPlayer || !isValidMonthly || amtNum <= 0) return null;
    const count = Math.floor(amtNum / feeNum);
    const startDate = new Date(selectedPlayer.paidUpToYear, selectedPlayer.paidUpToMonth - 1 + 1, 1);
    const endDate   = new Date(selectedPlayer.paidUpToYear, selectedPlayer.paidUpToMonth - 1 + count, 1);
    const fmt = (d) => `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
    return { count, label: count === 1 ? fmt(startDate) : `${fmt(startDate)} – ${fmt(endDate)}` };
  };

  const preview = monthsPreview();

  const ff = (key) => (e) => { setForm((p) => ({ ...p, [key]: e.target.value })); setError(""); };

  const handleSave = async () => {
    if (!form.playerId) { setError("Please select a player."); return; }
    if (!form.amount || amtNum <= 0) { setError("Enter a valid amount."); return; }
    if (isMonthly && !isValidMonthly) {
      setError(`Amount must be a multiple of ₹${feeNum.toLocaleString("en-IN")}.`); return;
    }
    setSaving(true); setError("");
    try {
      const payDate = form.paymentDate || new Date().toISOString().slice(0, 10);

      if (isMonthly) {
        await academyService.recordMonthlyPayment({
          playerId:       form.playerId,
          amount:         amtNum,
          paymentMode:    form.paymentMode,
          paymentDate:    payDate,
          transactionRef: form.transactionRef || null,
          notes:          form.notes || null,
        });
      } else {
        const pDate = new Date(payDate);
        await academyService.recordFee({
          playerId:       form.playerId,
          feeMonth:       pDate.getMonth() + 1,
          feeYear:        pDate.getFullYear(),
          totalAmount:    amtNum,
          paidAmount:     amtNum,
          discountAmount: 0,
          paymentMode:    form.paymentMode,
          paymentDate:    payDate,
          transactionRef: form.transactionRef || null,
          notes:          form.notes || null,
          feeType:        form.feeType,
        });
      }

      onSaved();
      onClose();
    } catch (e) {
      setError(e?.detail ?? e?.response?.data?.detail ?? e?.message ?? "Transaction failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <SoftTypography variant="h6" fontWeight="bold" color="dark">Add Transaction</SoftTypography>
        <SoftTypography variant="caption" color="text">
          Record any income — fees, donations, kits, services
        </SoftTypography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={dues}
              getOptionLabel={(o) => `${o.playerCode ?? ""} — ${o.playerName}`}
              inputValue={playerInput}
              onInputChange={(_, v) => setPlayerInput(v)}
              onChange={(_, v) => setForm((p) => ({ ...p, playerId: v?.playerId ?? "" }))}
              renderInput={(params) => (
                <TextField {...params} label="Player *" size="small" fullWidth
                  InputProps={{ ...params.InputProps, startAdornment: (
                    <><InputAdornment position="start"><Icon sx={{ fontSize: 18 }}>person_search</Icon></InputAdornment>{params.InputProps.startAdornment}</>
                  )}} />
              )}
            />
          </Grid>

          {selectedPlayer && (
            <Grid item xs={12}>
              <SoftBox p={1.5} borderRadius="lg"
                sx={{ backgroundColor: "#f8f9fa", display: "flex", gap: 3, flexWrap: "wrap" }}>
                <SoftBox>
                  <SoftTypography variant="caption" color="text">Batch</SoftTypography>
                  <SoftTypography variant="body2" fontWeight="bold" color="dark">{selectedPlayer.batchName ?? "—"}</SoftTypography>
                </SoftBox>
                <Divider orientation="vertical" flexItem />
                <SoftBox>
                  <SoftTypography variant="caption" color="text">Monthly Fee</SoftTypography>
                  <SoftTypography variant="body2" fontWeight="bold" color="dark">₹{Number(selectedPlayer.monthlyFee).toLocaleString("en-IN")}</SoftTypography>
                </SoftBox>
                {isMonthly && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <SoftBox>
                      <SoftTypography variant="caption" color="text">Paid Up To</SoftTypography>
                      <SoftTypography variant="body2" fontWeight="bold" color="dark">
                        {fmtMY(selectedPlayer.paidUpToMonth, selectedPlayer.paidUpToYear)}
                      </SoftTypography>
                    </SoftBox>
                    <Divider orientation="vertical" flexItem />
                    <SoftBox>
                      <SoftTypography variant="caption" color="text">Months Behind</SoftTypography>
                      <SoftTypography variant="body2" fontWeight="bold"
                        sx={{ color: selectedPlayer.monthsOwed > 0 ? "#d32f2f" : "#2e7d32" }}>
                        {selectedPlayer.monthsOwed}
                      </SoftTypography>
                    </SoftBox>
                  </>
                )}
              </SoftBox>
            </Grid>
          )}

          <Grid item xs={6}>
            <TextField select fullWidth label="Transaction Type" value={form.feeType}
              onChange={ff("feeType")} size="small">
              {TRANSACTION_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required label="Amount (₹)" type="number" value={form.amount}
              onChange={ff("amount")} size="small"
              helperText={isMonthly && selectedPlayer ? `Multiple of ₹${Number(selectedPlayer.monthlyFee).toLocaleString("en-IN")}` : ""}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>

          {isMonthly && form.amount && amtNum > 0 && (
            <Grid item xs={12}>
              {isValidMonthly && preview ? (
                <Alert severity="success" sx={{ py: 0.5 }}>
                  <SoftTypography variant="caption">
                    Covers <strong>{preview.count} month{preview.count !== 1 ? "s" : ""}</strong>: {preview.label}
                  </SoftTypography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <SoftTypography variant="caption">
                    Amount must be a multiple of ₹{Number(selectedPlayer?.monthlyFee ?? 0).toLocaleString("en-IN")}
                  </SoftTypography>
                </Alert>
              )}
            </Grid>
          )}

          <Grid item xs={6}>
            <TextField select fullWidth label="Payment Mode" value={form.paymentMode}
              onChange={ff("paymentMode")} size="small">
              {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Date" type="date" value={form.paymentDate}
              onChange={ff("paymentDate")} size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Transaction Reference" value={form.transactionRef}
              onChange={ff("transactionRef")} size="small" placeholder="Cheque no., UPI ref, etc." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notes" value={form.notes}
              onChange={ff("notes")} size="small" multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <SoftButton variant="text" color="secondary" onClick={onClose}>Cancel</SoftButton>
        <SoftButton variant="gradient" color="success" onClick={handleSave}
          disabled={saving || !form.playerId || !form.amount || (isMonthly && !isValidMonthly)}>
          {saving ? "Saving…" : "Submit"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
AddTransactionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  dues: PropTypes.array.isRequired,
};

// ── Player Quick Detail dialog ─────────────────────────────────────────────────
function PlayerDetailDialog({ open, onClose, player, onPay, onViewHistory }) {
  const [detail, setDetail]     = useState(null);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (!open || !player) return;
    setLoading(true);
    academyService.getPlayers()
      .then((list) => {
        const found = list.find((p) => p.id === player.playerId);
        setDetail(found ?? null);
      })
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [open, player]);

  if (!player) return null;

  const isDue = (player.monthsOwed ?? 0) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <SoftBox
        sx={{
          background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
          px: 3, py: 2.5, color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}
      >
        <SoftBox>
          <SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>
            {player.playerName}
          </SoftTypography>
          <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontFamily: "monospace" }}>
            {player.playerCode}
          </SoftTypography>
          <SoftBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
            <SoftBadge
              badgeContent={player.batchName ?? "No batch"}
              color="light" size="xs" container
              sx={{ "& .MuiBadge-badge": { background: "rgba(255,255,255,0.2)", color: "#fff" } }}
            />
            {player.ageGroup && (
              <SoftBadge badgeContent={player.ageGroup} color="light" size="xs" container
                sx={{ "& .MuiBadge-badge": { background: "rgba(255,255,255,0.2)", color: "#fff" } }} />
            )}
          </SoftBox>
        </SoftBox>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <Icon>close</Icon>
        </IconButton>
      </SoftBox>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <SoftBox py={4} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </SoftBox>
        ) : (
          <>
            {/* Fee summary strip */}
            <SoftBox display="flex" sx={{ borderBottom: "1px solid #f0f0f0" }}>
              <SoftBox flex={1} textAlign="center" py={1.5} sx={{ borderRight: "1px solid #f0f0f0" }}>
                <SoftTypography variant="caption" color="text" display="block">Monthly Fee</SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" color="dark">
                  ₹{Number(player.monthlyFee).toLocaleString("en-IN")}
                </SoftTypography>
              </SoftBox>
              <SoftBox flex={1} textAlign="center" py={1.5} sx={{ borderRight: "1px solid #f0f0f0" }}>
                <SoftTypography variant="caption" color="text" display="block">Paid Up To</SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" color="dark">
                  {fmtMY(player.paidUpToMonth, player.paidUpToYear)}
                </SoftTypography>
              </SoftBox>
              <SoftBox flex={1} textAlign="center" py={1.5}>
                <SoftTypography variant="caption" color="text" display="block">Total Due</SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: isDue ? "#d32f2f" : "#2e7d32" }}>
                  ₹{Number(player.totalDue).toLocaleString("en-IN")}
                </SoftTypography>
              </SoftBox>
            </SoftBox>

            {/* Player details */}
            <SoftBox px={3} py={2}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <SoftTypography variant="caption" color="text" display="block">Admission Date</SoftTypography>
                  <SoftTypography variant="body2" fontWeight="medium" color="dark">
                    {detail?.admissionDate ? String(detail.admissionDate).slice(0, 10) : "—"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={6}>
                  <SoftTypography variant="caption" color="text" display="block">Fee Plan</SoftTypography>
                  <SoftBox>
                    <SoftBadge
                      badgeContent={detail?.feePlan ?? "Standard"}
                      color={FEE_PLAN_COLOR[detail?.feePlan] ?? "info"}
                      size="xs" container
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={6}>
                  <SoftTypography variant="caption" color="text" display="block">Date of Birth</SoftTypography>
                  <SoftTypography variant="body2" fontWeight="medium" color="dark">
                    {detail?.dateOfBirth ? String(detail.dateOfBirth).slice(0, 10) : "—"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={6}>
                  <SoftTypography variant="caption" color="text" display="block">Status</SoftTypography>
                  <SoftBadge
                    badgeContent={detail?.status ?? "Active"}
                    color={{ Active: "success", Inactive: "secondary", Trial: "warning" }[detail?.status] ?? "info"}
                    size="xs" container
                  />
                </Grid>
                {detail?.parentName && (
                  <Grid item xs={6}>
                    <SoftTypography variant="caption" color="text" display="block">Parent / Guardian</SoftTypography>
                    <SoftTypography variant="body2" fontWeight="medium" color="dark">{detail.parentName}</SoftTypography>
                  </Grid>
                )}
                {detail?.parentPhone && (
                  <Grid item xs={6}>
                    <SoftTypography variant="caption" color="text" display="block">Phone</SoftTypography>
                    <SoftTypography variant="body2" fontWeight="medium" color="dark">{detail.parentPhone}</SoftTypography>
                  </Grid>
                )}
                {isDue && (
                  <Grid item xs={12}>
                    <SoftBox
                      sx={{
                        backgroundColor: "#fff3f3", border: "1px solid #ffcdd2",
                        borderRadius: 2, px: 2, py: 1, display: "flex", gap: 2, flexWrap: "wrap",
                      }}
                    >
                      <SoftBox>
                        <SoftTypography variant="caption" color="error" display="block">Months Behind</SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold" color="error">{player.monthsOwed}</SoftTypography>
                      </SoftBox>
                      <Divider orientation="vertical" flexItem />
                      <SoftBox>
                        <SoftTypography variant="caption" color="error" display="block">Outstanding</SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold" color="error">
                          ₹{Number(player.totalDue).toLocaleString("en-IN")}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                )}
              </Grid>
            </SoftBox>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <SoftButton variant="outlined" color="info" size="small" startIcon={<Icon>history</Icon>}
          onClick={() => { onClose(); onViewHistory(player); }}>
          View History
        </SoftButton>
        {isDue && (
          <SoftButton variant="gradient" color="success" size="small" startIcon={<Icon>payments</Icon>}
            onClick={() => { onClose(); onPay(player); }}>
            Record Payment
          </SoftButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
PlayerDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  player: PropTypes.object,
  onPay: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
};

// ── Sortable column header ─────────────────────────────────────────────────────
function SortableHeader({ label, field, sortField, sortDir, onSort, flex, textAlign = "left" }) {
  const active = sortField === field;
  return (
    <Tooltip title={`Sort by ${label}`}>
      <SoftBox
        sx={{ flex, textAlign, cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center",
          gap: 0.3, justifyContent: textAlign === "right" ? "flex-end" : textAlign === "center" ? "center" : "flex-start" }}
        onClick={() => onSort(field)}
      >
        <SoftTypography variant="caption" fontWeight="bold" color={active ? "primary" : "text"}>
          {label}
        </SoftTypography>
        <Icon sx={{ fontSize: "0.75rem !important", color: active ? "#1a73e8" : "#bbb", mt: "1px" }}>
          {active ? (sortDir === "asc" ? "arrow_upward" : "arrow_downward") : "unfold_more"}
        </Icon>
      </SoftBox>
    </Tooltip>
  );
}
SortableHeader.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  sortField: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  textAlign: PropTypes.string,
};

// ── Due row ────────────────────────────────────────────────────────────────────
function DueRow({ player, onPay, onViewHistory, onViewDetail }) {
  const isDue = player.monthsOwed > 0;
  return (
    <SoftBox display="flex" alignItems="center" py={1.5} px={2}
      sx={{ borderBottom: "1px solid #f5f5f5", "&:hover": { backgroundColor: "rgba(26,115,232,0.02)" } }}>
      <SoftTypography variant="caption" color="text"
        sx={{ flex: "0 0 130px", fontFamily: "monospace", fontSize: "0.68rem", fontWeight: 600 }}>
        {player.playerCode ?? "—"}
      </SoftTypography>
      <SoftTypography
        variant="button" fontWeight="medium"
        sx={{ flex: 1, color: COLORS.primary.hex, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        onClick={() => onViewDetail(player)}
      >
        {player.playerName}
      </SoftTypography>
      <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ flex: "0 0 160px" }}>
        {player.batchName ?? "No batch"}
      </SoftTypography>
      <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 100px", textAlign: "right" }}>
        ₹{Number(player.monthlyFee).toLocaleString("en-IN")}
      </SoftTypography>
      <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 110px", textAlign: "center" }}>
        {fmtMY(player.paidUpToMonth, player.paidUpToYear)}
      </SoftTypography>
      <SoftBox sx={{ flex: "0 0 90px", textAlign: "center" }}>
        {isDue
          ? <SoftBadge badgeContent={`${player.monthsOwed}m behind`} color="error" size="xs" container />
          : <SoftBadge badgeContent="Up to date" color="success" size="xs" container />
        }
      </SoftBox>
      <SoftTypography variant="caption" fontWeight="bold"
        sx={{ flex: "0 0 100px", textAlign: "right", color: isDue ? "#d32f2f" : "#2e7d32" }}>
        ₹{Number(player.totalDue).toLocaleString("en-IN")}
      </SoftTypography>
      <SoftBox sx={{ flex: "0 0 100px", textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
        <SoftButton variant="text" color="success" iconOnly size="small"
          title={isDue ? "Record Payment" : "Record Future Payment"} onClick={() => onPay(player)}>
          <Icon>payments</Icon>
        </SoftButton>
        <SoftButton variant="text" color="info" iconOnly size="small" title="Transaction History" onClick={() => onViewHistory(player)}>
          <Icon>history</Icon>
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}
DueRow.propTypes = {
  player: PropTypes.shape({
    playerId: PropTypes.string,
    playerCode: PropTypes.string,
    playerName: PropTypes.string,
    batchName: PropTypes.string,
    ageGroup: PropTypes.string,
    monthlyFee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paidUpToMonth: PropTypes.number,
    paidUpToYear: PropTypes.number,
    deadlineMonth: PropTypes.number,
    deadlineYear: PropTypes.number,
    monthsOwed: PropTypes.number,
    totalDue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onPay: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
  onViewDetail: PropTypes.func.isRequired,
};

// ── Main ───────────────────────────────────────────────────────────────────────
function AcademyFees() {
  const [dues, setDues]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterStudentName, setFilterStudentName] = useState("");
  const [filterStatus, setFilterStatus]   = useState("all");

  const [sortField, setSortField]         = useState("monthsOwed");
  const [sortDir, setSortDir]             = useState("desc");

  const [payDialog, setPayDialog]         = useState(false);
  const [payPlayer, setPayPlayer]         = useState(null);
  const [payForm, setPayForm]             = useState(emptyPayForm);
  const [saving, setSaving]               = useState(false);
  const [payError, setPayError]           = useState("");

  const [historyPlayer, setHistoryPlayer] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [editRecord, setEditRecord]       = useState(null);
  const [editDialog, setEditDialog]       = useState(false);

  const [detailPlayer, setDetailPlayer]   = useState(null);
  const [detailDialog, setDetailDialog]   = useState(false);

  const [txnDialog, setTxnDialog]         = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    academyService.getPlayerFeesDue()
      .then((d) => setDues(Array.isArray(d) ? d : []))
      .catch(() => setDues([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPay = (player) => {
    setPayPlayer(player);
    setPayForm({ ...emptyPayForm, amount: String(player.totalDue ?? "") });
    setPayError("");
    setPayDialog(true);
  };

  const openHistory = (player) => {
    setHistoryPlayer(player);
    setHistoryDialog(true);
  };

  const openDetail = (player) => {
    setDetailPlayer(player);
    setDetailDialog(true);
  };

  const handleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => d === "asc" ? "desc" : "asc");
        return field;
      }
      setSortDir("asc");
      return field;
    });
  };

  const amtNum = Number(payForm.amount);
  const feeNum = payPlayer ? Number(payPlayer.monthlyFee) : 1;
  const isValidAmount = amtNum > 0 && feeNum > 0 && amtNum % feeNum === 0;

  const monthsCoveredPreview = () => {
    if (!payPlayer || !isValidAmount) return null;
    const count = Math.floor(amtNum / feeNum);
    const startDate = new Date(payPlayer.paidUpToYear, payPlayer.paidUpToMonth - 1 + 1, 1);
    const endDate   = new Date(payPlayer.paidUpToYear, payPlayer.paidUpToMonth - 1 + count, 1);
    const fmt = (d) => `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
    return { count, label: count === 1 ? fmt(startDate) : `${fmt(startDate)} – ${fmt(endDate)}` };
  };

  const preview = monthsCoveredPreview();

  const handlePay = async () => {
    if (!isValidAmount) {
      setPayError(`Amount must be a multiple of ₹${feeNum.toLocaleString("en-IN")}.`);
      return;
    }
    setSaving(true); setPayError("");
    try {
      await academyService.recordMonthlyPayment({
        playerId:       payPlayer.playerId,
        amount:         amtNum,
        paymentMode:    payForm.paymentMode,
        paymentDate:    payForm.paymentDate,
        transactionRef: payForm.transactionRef || null,
        notes:          payForm.notes || null,
      });
      setPayDialog(false);
      load();
    } catch (e) {
      setPayError(e?.detail ?? e?.message ?? "Payment failed.");
    } finally { setSaving(false); }
  };

  const filtered = dues.filter((d) => {
    if (filterStudentName && !(d.playerName ?? "").toLowerCase().includes(filterStudentName.toLowerCase())) return false;
    if (filterStatus === "due" && d.monthsOwed === 0) return false;
    if (filterStatus === "current" && d.monthsOwed > 0) return false;
    return true;
  });

  const SORT_FN = {
    playerCode:   (a, b) => (a.playerCode ?? "").localeCompare(b.playerCode ?? ""),
    playerName:   (a, b) => (a.playerName ?? "").localeCompare(b.playerName ?? ""),
    batchName:    (a, b) => (a.batchName ?? "").localeCompare(b.batchName ?? ""),
    monthlyFee:   (a, b) => Number(a.monthlyFee) - Number(b.monthlyFee),
    monthsOwed:   (a, b) => Number(a.monthsOwed) - Number(b.monthsOwed),
    totalDue:     (a, b) => Number(a.totalDue) - Number(b.totalDue),
  };

  const sorted = [...filtered].sort((a, b) => {
    const fn = SORT_FN[sortField];
    const result = fn ? fn(a, b) : 0;
    return sortDir === "asc" ? result : -result;
  });

  const totalDue     = filtered.reduce((s, d) => s + Number(d.totalDue), 0);
  const dueCount     = filtered.filter((d) => d.monthsOwed > 0).length;
  const currentCount = filtered.filter((d) => d.monthsOwed === 0).length;
  const totalMonthly = filtered.reduce((s, d) => s + Number(d.monthlyFee), 0);

  const sortProps = { sortField, sortDir, onSort: handleSort };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" sx={{ color: TYPOGRAPHY.page.titleColor }}>Transactions</SoftTypography>
            <SoftTypography variant="body2" sx={{ color: TYPOGRAPHY.page.subtitleColor }}>
              All income — monthly fees, admissions, donations, kits, services · click a name to view details
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} flexWrap="wrap">
            <SoftButton variant="outlined" color="primary" startIcon={<Icon>download</Icon>}
              onClick={() => downloadDuesPDF(filtered)} disabled={filtered.length === 0}>
              Download Report
            </SoftButton>
            <SoftButton variant="gradient" color="success" startIcon={<Icon>add_circle</Icon>}
              onClick={() => setTxnDialog(true)}>
              Add Transaction
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Players" }}
                count={String(filtered.length)}
                percentage={{ color: "primary", text: `${dueCount} with dues` }}
                icon={{ color: "primary", component: "group" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Due" }}
                count={`₹${Math.round(totalDue / 1000)}K`}
                percentage={{ color: totalDue > 0 ? "error" : "success", text: `${dueCount} pending` }}
                icon={{ color: "error", component: "money_off" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Up to Date" }}
                count={String(currentCount)}
                percentage={{ color: "success", text: "no dues pending" }}
                icon={{ color: "success", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Monthly Income" }}
                count={`₹${Math.round(totalMonthly / 1000)}K`}
                percentage={{ color: "info", text: "projected" }}
                icon={{ color: "info", component: "trending_up" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Player dues table */}
        <Card>
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center"
            sx={{ borderBottom: "1px solid #f0f0f0" }}>
            <TextField select size="small" label="Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="all">All Players</MenuItem>
              <MenuItem value="due">With Dues</MenuItem>
              <MenuItem value="current">Up to Date</MenuItem>
            </TextField>
            <TextField size="small" label="Search Player" placeholder="Player name" value={filterStudentName}
              onChange={(e) => setFilterStudentName(e.target.value)} sx={{ minWidth: 180 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon sx={{ fontSize: 18 }}>search</Icon></InputAdornment> }} />
            <SoftTypography variant="caption" color="text" sx={{ ml: "auto" }}>
              {dueCount} due · {currentCount} up to date
            </SoftTypography>
          </SoftBox>

          {/* Column headers */}
          <SoftBox display="flex" alignItems="center" px={2} py={1}
            sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <SortableHeader label="PLAYER CODE" field="playerCode" flex="0 0 130px" {...sortProps} />
            <SortableHeader label="STUDENT" field="playerName" flex={1} {...sortProps} />
            <SortableHeader label="BATCH" field="batchName" flex="0 0 160px" {...sortProps} />
            <SortableHeader label="MONTHLY FEE" field="monthlyFee" flex="0 0 100px" textAlign="right" {...sortProps} />
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px", textAlign: "center" }}>PAID UP TO</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 90px", textAlign: "center" }}>STATUS</SoftTypography>
            <SortableHeader label="TOTAL DUE" field="totalDue" flex="0 0 100px" textAlign="right" {...sortProps} />
            <SortableHeader label="MONTHS BEHIND" field="monthsOwed" flex="0 0 100px" textAlign="center" {...sortProps} />
          </SoftBox>

          {loading ? (
            <FootballLoader label="Loading transactions…" />
          ) : (
            <SoftBox
              sx={{
                maxHeight: 520,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 5 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.12)", borderRadius: 3 },
              }}
            >
              {sorted.length === 0 ? (
                <SoftBox p={4} textAlign="center">
                  <SoftTypography variant="caption" color="text">No players found</SoftTypography>
                </SoftBox>
              ) : (
                sorted.map((d) => (
                  <DueRow key={d.playerId} player={d} onPay={openPay} onViewHistory={openHistory} onViewDetail={openDetail} />
                ))
              )}
            </SoftBox>
          )}

          {filtered.length > 0 && (
            <SoftBox display="flex" alignItems="center" px={2} py={1.5}
              sx={{ borderTop: "2px solid #f0f0f0", backgroundColor: "#fafafa" }}>
              <SoftBox sx={{ flex: "0 0 130px" }} />
              <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: 1 }}>
                TOTAL ({filtered.length} players)
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 160px" }} />
              <SoftBox sx={{ flex: "0 0 100px" }} />
              <SoftBox sx={{ flex: "0 0 110px" }} />
              <SoftBox sx={{ flex: "0 0 90px" }} />
              <SoftTypography variant="caption" fontWeight="bold"
                sx={{ flex: "0 0 100px", textAlign: "right", color: totalDue > 0 ? "#d32f2f" : "#2e7d32" }}>
                ₹{totalDue.toLocaleString("en-IN")}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 100px" }} />
            </SoftBox>
          )}
        </Card>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Add Transaction dialog ── */}
      <AddTransactionDialog
        open={txnDialog}
        onClose={() => setTxnDialog(false)}
        onSaved={load}
        dues={dues}
      />

      {/* ── Player Detail dialog ── */}
      <PlayerDetailDialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        player={detailPlayer}
        onPay={openPay}
        onViewHistory={openHistory}
      />

      {/* ── Quick Monthly Payment dialog (from DueRow pay button) ── */}
      <Dialog open={payDialog} onClose={() => setPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">Record Monthly Fee Payment</SoftTypography>
            {payPlayer && (
              <SoftTypography variant="caption" color="text">
                {payPlayer.playerName} · {payPlayer.batchName ?? "No batch"}
              </SoftTypography>
            )}
          </SoftBox>
        </DialogTitle>
        <DialogContent dividers>
          {payPlayer && (
            <SoftBox mb={2} p={1.5} borderRadius="lg"
              sx={{ backgroundColor: "#f8f9fa", display: "flex", gap: 3, flexWrap: "wrap" }}>
              <SoftBox>
                <SoftTypography variant="caption" color="text">Monthly Fee</SoftTypography>
                <SoftTypography variant="body2" fontWeight="bold" color="dark">
                  ₹{Number(payPlayer.monthlyFee).toLocaleString("en-IN")}
                </SoftTypography>
              </SoftBox>
              <Divider orientation="vertical" flexItem />
              <SoftBox>
                <SoftTypography variant="caption" color="text">Paid Up To</SoftTypography>
                <SoftTypography variant="body2" fontWeight="bold" color="dark">
                  {fmtMY(payPlayer.paidUpToMonth, payPlayer.paidUpToYear)}
                </SoftTypography>
              </SoftBox>
              <Divider orientation="vertical" flexItem />
              <SoftBox>
                <SoftTypography variant="caption" color="text">Months Behind</SoftTypography>
                <SoftTypography variant="body2" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                  {payPlayer.monthsOwed}
                </SoftTypography>
              </SoftBox>
              <Divider orientation="vertical" flexItem />
              <SoftBox>
                <SoftTypography variant="caption" color="text">Total Due</SoftTypography>
                <SoftTypography variant="body2" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                  ₹{Number(payPlayer.totalDue).toLocaleString("en-IN")}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          )}

          {payError && <Alert severity="error" sx={{ mb: 2 }}>{payError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Amount (₹)" type="number" value={payForm.amount}
                onChange={(e) => { setPayForm({ ...payForm, amount: e.target.value }); setPayError(""); }}
                size="small"
                helperText={payPlayer ? `Must be a multiple of ₹${Number(payPlayer.monthlyFee).toLocaleString("en-IN")}` : ""}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            {payForm.amount && Number(payForm.amount) > 0 && (
              <Grid item xs={12}>
                {isValidAmount && preview ? (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    <SoftTypography variant="caption">
                      Covers <strong>{preview.count} month{preview.count !== 1 ? "s" : ""}</strong>: {preview.label}
                    </SoftTypography>
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <SoftTypography variant="caption">
                      Amount must be a multiple of ₹{Number(payPlayer?.monthlyFee ?? 0).toLocaleString("en-IN")}
                    </SoftTypography>
                  </Alert>
                )}
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField select fullWidth label="Payment Mode" value={payForm.paymentMode}
                onChange={(e) => setPayForm({ ...payForm, paymentMode: e.target.value })} size="small">
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Date" type="date" value={payForm.paymentDate}
                onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })}
                size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Transaction Reference" value={payForm.transactionRef}
                onChange={(e) => setPayForm({ ...payForm, transactionRef: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={payForm.notes}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                size="small" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setPayDialog(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="success" onClick={handlePay} disabled={saving || !isValidAmount}>
            {saving ? "Saving…" : "Record Payment"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Per-player history dialog ── */}
      <PlayerHistoryDialog
        open={historyDialog}
        onClose={() => setHistoryDialog(false)}
        player={historyPlayer}
        onEdit={(rec) => { setHistoryDialog(false); setEditRecord(rec); setEditDialog(true); }}
        onDeleted={load}
      />

      {/* ── Edit transaction record dialog ── */}
      <EditFeeDialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        record={editRecord}
        onSaved={() => { load(); }}
      />
    </DashboardLayout>
  );
}

export default AcademyFees;
